import { useCallback, useEffect, useState } from "react";
import { Camera, Check, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Inline media-consent editor for the profile page. Reads current state via
 * /api/auth/me (or from a prop `initial`), lets the user flip granted ↔ declined
 * on the fly, and persists via POST /api/me/media-consent. Withdrawing consent
 * (granted → declined) drops a pinned coach-note on the profile server-side —
 * the user sees an explanatory toast so they know coaches will be informed.
 */
export default function MediaConsentEditor({ initial, onChange }) {
  const [status, setStatus] = useState(initial?.status || null);
  const [decidedAt, setDecidedAt] = useState(initial?.decided_at || null);
  const [saving, setSaving] = useState(null); // "granted" | "declined" | null
  const [loading, setLoading] = useState(!initial);

  const bootstrap = useCallback(async () => {
    if (initial) return;
    setLoading(true);
    try {
      const { data } = await api.get("/auth/me");
      const mc = data.media_consent;
      if (mc) {
        setStatus(mc.status || null);
        setDecidedAt(mc.decided_at || null);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [initial]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const set = async (next) => {
    if (saving || next === status) return;
    setSaving(next);
    try {
      const { data } = await api.post("/me/media-consent", { status: next });
      const mc = data.media_consent || {};
      setStatus(mc.status);
      setDecidedAt(mc.decided_at);
      if (onChange) onChange(mc);
      if (next === "granted") {
        toast.success("Media consent granted — coaches can now include you in analysis clips.");
      } else {
        toast("Media consent withdrawn.", {
          description: "Coaches will be flagged before every session so they know not to record you.",
        });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save. Please try again.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className="card-surface p-6" data-testid="media-consent-editor">
      <div className="flex items-start gap-2 mb-3">
        <Camera className="w-4 h-4 text-[#CCFF00] mt-0.5" />
        <div>
          <div className="label-mono">MEDIA CONSENT</div>
          <p className="text-xs text-[#A3A3A3] mt-1 leading-relaxed">
            Coaches may want to take short photos or clips during sessions for analysis
            &amp; debriefing. You can change your answer any time.
          </p>
        </div>
      </div>
      {loading ? (
        <div className="text-[10px] uppercase tracking-wider text-[#525252]">Loading…</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          <Choice
            testid="media-consent-editor-granted"
            active={status === "granted"}
            color="#CCFF00"
            icon={Check}
            label="Yes, I consent"
            sub="Coaches can record video / photos of me."
            saving={saving === "granted"}
            onClick={() => set("granted")}
          />
          <Choice
            testid="media-consent-editor-declined"
            active={status === "declined"}
            color="#FF4500"
            icon={Lock}
            label="No, please don't"
            sub="No photos, no video. Coaches will be reminded on every booking."
            saving={saving === "declined"}
            onClick={() => set("declined")}
          />
        </div>
      )}
      {decidedAt && (
        <div className="mt-3 text-[10px] text-[#525252] uppercase tracking-wider">
          Last updated: {new Date(decidedAt).toLocaleString()}
        </div>
      )}
    </section>
  );
}

function Choice({ testid, active, color, icon: Icon, label, sub, saving, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      disabled={saving}
      className="text-left p-4 border transition-all disabled:opacity-70"
      style={active
        ? { borderColor: color, background: `${color}12` }
        : { borderColor: "#262626" }}
    >
      <div className="flex items-center gap-2 mb-1">
        {saving
          ? <Loader2 className="w-4 h-4 animate-spin" style={{ color }} />
          : <Icon className="w-4 h-4" style={{ color: active ? color : "#A3A3A3" }} />}
        <div className="label-mono" style={{ color: active ? color : "#A3A3A3" }}>{label}</div>
      </div>
      <div className="text-xs text-[#A3A3A3] leading-relaxed">{sub}</div>
    </button>
  );
}
